import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Support2.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useNavigate } from "react-router-dom";
import { useCustomer } from "../contexts/CustomerContext";

const SupportCenter = () => {
  const { refreshUser } = useCustomer();
  const [activePage, setActivePage] = useState("home");
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    position: "",
    company: "",
    companyId: "",
    companyName: "",
    phoneNumber: "",
    address: "",
    password: "",
    confirmPassword: "",
    taxno: "",
    companyManual: "",
    addressManual: "",
    timeZone: "",
  });

  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [allowCompanySelect, setAllowCompanySelect] = useState(false);
  const [showNewCompanyInput, setShowNewCompanyInput] = useState(false);
  const [companySearchStatus, setCompanySearchStatus] = useState("idle");
  const [isRegistering, setIsRegistering] = useState(false);

  const [signinPopup, setSigninPopup] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    redirectAfterClose: false,
    redirectState: null,
  });

  const showSigninPopup = ({
    type = "info",
    title = "",
    message = "",
    redirectAfterClose = false,
    redirectState = null,
  }) => {
    setSigninPopup({
      show: true,
      type,
      title,
      message,
      redirectAfterClose,
      redirectState,
    });
  };

  const closeSigninPopup = () => {
    const shouldRedirect = signinPopup.redirectAfterClose;
    const redirectState = signinPopup.redirectState;

    setSigninPopup({
      show: false,
      type: "",
      message: "",
      title: "",
      redirectAfterClose: false,
      redirectState: null,
    });
    if (shouldRedirect) {
      navigate("/customer/tickets", {
        state: redirectState,
      });
    }
  };

  const [registerPopup, setRegisterPopup] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    reloadAfterClose: false,
  });

  const showRegisterPopup = ({
    type = "info",
    title = "",
    message = "",
    reloadAfterClose = false,
  }) => {
    setRegisterPopup({
      show: true,
      type,
      title,
      message,
      reloadAfterClose,
    });
  };

  const closeRegisterPopup = () => {
    const shouldReload = registerPopup.reloadAfterClose;

    setRegisterPopup({
      show: false,
      type: "",
      title: "",
      message: "",
      reloadAfterClose: false,
    });

    if (shouldReload) {
      window.location.reload();
    }
  };

  const handleEmailSubmit = async () => {
    try {
      if (!validateEmail(formData.email)) {
        showRegisterPopup({
          type: "error",
          title: "Invalid email address.",
          message: "Please enter a valid email address.",
        });
        return;
      }

      await axios.post(
        "/otp",
        {
          email: formData.email.toLowerCase(),
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      setIsOtpSent(true);
      setTimer(180);
      setIsOtpExpired(false);
      setOtp("");
      setOtpError("");

      showRegisterPopup({
        type: "success",
        title: "OTP sent successfully.",
        message: "OTP has been sent to your email!",
      });
    } catch (err) {
      console.error("Error while sending OTP:", err);

      const backendMessage = err.response?.data?.error;

      if (backendMessage) {
        showRegisterPopup({
          type: "error",
          title: backendMessage.includes("already registered")
            ? "This email is already in use."
            : "Failed to send OTP.",
          message: backendMessage,
        });
        return;
      }

      showRegisterPopup({
        type: "error",
        title: "Failed to send OTP.",
        message: "Failed to send OTP. Please try again later.",
      });
    }
  };

  const validateEmail = (email) => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return regex.test(normalizedEmail);
  };

  const handleSignin = async () => {
    const emailInput = document.getElementById("email");
    const passwordInput = document.getElementById("password");

    if (!emailInput.value.trim() || !passwordInput.value.trim()) {
      showSigninPopup({
        type: "error",
        title: "เข้าสู่ระบบไม่สำเร็จ",
        message: "Please make sure to provide both your email and password.",
      });
      return;
    }

    try {
      const response = await fetch("/auth/signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: emailInput.value.trim().toLowerCase(),
          password: passwordInput.value.trim(),
        }),
      });

      const text = await response.text();
      const result = (() => {
        try {
          return JSON.parse(text);
        } catch {
          return {};
        }
      })();

      if (response.ok) {
        const authenticated = await refreshUser();

        if (!authenticated) {
          showSigninPopup({
            type: "error",
            title: "เข้าสู่ระบบไม่สำเร็จ",
            message: "ไม่สามารถตรวจสอบสถานะการเข้าสู่ระบบได้",
          });

          return;
        }

        showSigninPopup({
          type: "success",
          title: "เข้าสู่ระบบสำเร็จ",
          message: result.message || "Signed in",
          redirectAfterClose: true,
          redirectState: {
            userId: result.userId,
            userEmail: result.userEmail,
          },
        });
      } else {
        showSigninPopup({
          type: "error",
          title: "เข้าสู่ระบบไม่สำเร็จ",
          message: result.message || "เกิดข้อผิดพลาดในการเข้าสู่ระบบ",
        });
      }
    } catch (error) {
      console.error("Error during sign-in:", error);

      showSigninPopup({
        type: "error",
        title: "เกิดข้อผิดพลาด",
        message: "เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองอีกครั้ง",
      });
    }
  };

  const handleOtpSubmit = async () => {
    try {
      const response = await axios.post(
        "/otp/verify-otp",
        {
          email: formData.email.toLowerCase(),
          otp,
        },
      );
      if (response.data.success) {
        setActivePage("form");
        setOtpError("");
      } else {
        setOtpError("Incorrect OTP. Please try again.");
      }
    } catch (err) {
      const backendError =
        err.response?.data?.error || err.response?.data?.message;

      setOtpError(
        backendError || "เกิดข้อผิดพลาดในการตรวจสอบ OTP กรุณาลองใหม่อีกครั้ง",
      );
    }
  };

  const handleInputChange = (e) => {
    const { name, id, value, dataset } = e.target;

    const fieldName = name || id || dataset.even;

    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: fieldName === "email" ? value.toLowerCase() : value,
    }));
  };

  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const [timer, setTimer] = useState(180);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [otpError, setOtpError] = useState("");
  // const addressToSubmit = formData.address || formData.addressManual;
  // const companyToSubmit = formData.companyId || formData.companyManual;

  useEffect(() => {
    let timerInterval;
    if (isOtpSent && timer > 0) {
      timerInterval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsOtpExpired(true);
    }
    return () => clearInterval(timerInterval);
  }, [isOtpSent, timer]);

  useEffect(() => {
    if (formData.password.trim() && formData.confirmPassword.trim()) {
      validatePassword(
        formData.password.trim(),
        formData.confirmPassword.trim(),
      );
    }
  }, [formData.password, formData.confirmPassword]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      validatePassword(
        updatedData.password.trim(),
        updatedData.confirmPassword.trim(),
      );
      return updatedData;
    });
  };

  const validatePassword = (password, confirmPassword) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    password = password?.trim() || "";
    confirmPassword = confirmPassword?.trim() || "";

    if (password.length < minLength) {
      setPasswordError("รหัสผ่านต้องมีความยาวอย่างน้อย 8 ตัวอักษร");
      return false;
    }

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      setPasswordError(
        "รหัสผ่านต้องมีตัวพิมพ์ใหญ่, ตัวพิมพ์เล็ก, ตัวเลข และอักขระพิเศษ",
      );
      return false;
    }

    setPasswordError("");

    if (password !== confirmPassword) {
      setConfirmPasswordError("รหัสผ่านไม่ตรงกัน❌");
      return false;
    } else {
      setConfirmPasswordError("");
    }

    return true;
  };

  const isEmpty = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    return false;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const addressToSubmit = formData.address || formData.addressManual;
    const companyToSubmit = formData.companyId || formData.companyManual;

    const requiredFields = {
      fullName: formData.fullName,
      position: formData.position,
      phoneNumber: formData.phoneNumber,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      company: companyToSubmit,
      address: addressToSubmit,
      timeZone: formData.timeZone,
    };

    for (const [fieldName, value] of Object.entries(requiredFields)) {
      if (isEmpty(value)) {
        showRegisterPopup({
          type: "error",
          title: "กรอกข้อมูลไม่ครบ",
          message: `กรุณากรอกข้อมูลให้ครบทุกช่อง❌ (พบปัญหาที่ ${fieldName})`,
        });
        return;
      }
    }

    const phoneRegex = /^[0-9]{10}$/;

    if (!phoneRegex.test(formData.phoneNumber)) {
      showRegisterPopup({
        type: "error",
        title: "เบอร์โทรศัพท์ไม่ถูกต้อง",
        message: "Please enter a valid phone number (10 digits only)📞",
      });
      return;
    }

    const nameRegex = /^[A-Za-zก-๙\s]+$/;

    if (!nameRegex.test(formData.fullName.trim())) {
      showRegisterPopup({
        type: "error",
        title: "ชื่อไม่ถูกต้อง",
        message: "ชื่อไม่ควรมีตัวเลขหรืออักขระพิเศษ❌",
      });
      return;
    }

    if (/\s/.test(formData.password)) {
      showRegisterPopup({
        type: "error",
        title: "รหัสผ่านไม่ถูกต้อง",
        message: "รหัสผ่านไม่ควรมีช่องว่าง (space) ❌",
      });
      return;
    }

    if (
      !validatePassword(
        formData.password.trim(),
        formData.confirmPassword.trim(),
      )
    ) {
      showRegisterPopup({
        type: "error",
        title: "สมัครสมาชิกไม่สำเร็จ",
        message:
          "Registration failed: Password does not meet the required conditions ❌",
      });
      return;
    }

    if (isRegistering) {
      return;
    }

    setIsRegistering(true);

    try {
      const response = await fetch("/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase(),
          name: formData.fullName.trim(),
          position: formData.position.trim(),
          companyId: formData.companyId || null,
          companyManual: formData.companyManual?.trim() || null,
          taxno: formData.taxno?.trim() || null,
          phone: formData.phoneNumber.trim(),
          address: addressToSubmit.trim(),
          timezone: formData.timeZone?.trim() || "",
          password: formData.password,
        }),
      });

      const responseText = await response.text();

      let data = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {};
      }
      if (response.ok) {
        setFormData({
          email: "",
          fullName: "",
          position: "",
          companyId: "",
          companyName: "",
          companyManual: "",
          taxno: "",
          phoneNumber: "",
          address: "",
          addressManual: "",
          timeZone: "",
          password: "",
          confirmPassword: "",
        });

        showRegisterPopup({
          type: "success",
          title: "สมัครสมาชิกสำเร็จ",
          message: "Registration successful. ✅",
          reloadAfterClose: true,
        });
      } else {
        showRegisterPopup({
          type: "error",
          title: "สมัครสมาชิกไม่สำเร็จ",
          message:
            data.error || data.message || "เกิดข้อผิดพลาดในการสมัครสมาชิก",
        });
      }
    } catch (error) {
      console.error("Error:", error);

      showRegisterPopup({
        type: "error",
        title: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้",
        message: "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์",
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const [options, setOptions] = useState({
    company: [],
    address: [],
  });

  const [loadingCompany, setLoadingCompany] = useState(false);

  const handleSearchCompany = async () => {
    const normalizedTaxNo = String(formData.taxno || "")
      .replace(/\D/g, "")
      .trim();

    if (!/^\d{13}$/.test(normalizedTaxNo)) {
      showRegisterPopup({
        type: "error",
        title: "Tax ID ไม่ถูกต้อง",
        message: "กรุณากรอกหมายเลข Tax ID เป็นตัวเลข 13 หลัก",
      });

      return;
    }

    try {
      setLoadingCompany(true);
      setCompanySearchStatus("idle");
      setAllowCompanySelect(false);
      setShowNewCompanyInput(false);

      setOptions((prev) => ({
        ...prev,
        company: [],
      }));

      setFormData((prev) => ({
        ...prev,
        taxno: normalizedTaxNo,
        companyId: "",
        companyName: "",
        companyManual: "",
      }));

      const res = await axios.get(
        "/api/customer-company/search",
        {
          params: {
            tax: normalizedTaxNo,
          },
        },
      );

      const list = Array.isArray(res.data?.companies) ? res.data.companies : [];

      if (list.length === 0) {
        setCompanySearchStatus("not-found");
        setAllowCompanySelect(false);
        setShowNewCompanyInput(true);

        showRegisterPopup({
          type: "info",
          title: "ไม่พบบริษัทในระบบ",
          message:
            "ไม่พบ Tax ID นี้ กรุณากรอกชื่อบริษัทใหม่ในช่อง Company ด้านล่าง",
        });

        return;
      }

      const companyOptions = list
        .filter((company) => company?.id && String(company?.name || "").trim())
        .map((company) => ({
          id: company.id,
          name: String(company.name).trim(),
        }));

      setOptions((prev) => ({
        ...prev,
        company: companyOptions,
      }));

      setCompanySearchStatus("found");
      setAllowCompanySelect(true);
      setShowNewCompanyInput(false);

      if (companyOptions.length === 1) {
        setFormData((prev) => ({
          ...prev,
          companyId: companyOptions[0].id,
          companyName: companyOptions[0].name,
          companyManual: "",
        }));
      }
    } catch (err) {
      console.error("ค้นหาบริษัทผิดพลาด:", err);

      setCompanySearchStatus("idle");
      setAllowCompanySelect(false);
      setShowNewCompanyInput(false);

      showRegisterPopup({
        type: "error",
        title: "ค้นหาบริษัทไม่สำเร็จ",
        message:
          err.response?.data?.message ||
          "เกิดข้อผิดพลาดในการค้นหาบริษัท กรุณาลองใหม่อีกครั้ง",
      });
    } finally {
      setLoadingCompany(false);
    }
  };

  const handleOpenSignInPage = async () => {
    try {
      await axios.get("/api/customer/check-auth", {
        withCredentials: true,
      });

      navigate("/customer/tickets");
    } catch (err) {
      setActivePage("sign-in");
    }
  };

  return (
    <>
      <div className="about-container2">
        <div className="support-navigation">
          <div className="nav-center">
            <button
              className={`support-nav-button ${
                activePage === "home" ? "active" : ""
              }`}
              onClick={() => setActivePage("home")}
            >
              <i className="fas fa-home"></i> Support Center Home
            </button>
          </div>
          <div className="auth-container">
            <p>Guest User</p>
            <p>|</p>
            <button className="link-button" onClick={handleOpenSignInPage}>
              Sign In
            </button>
          </div>
        </div>

        <main className="support-content">
          {activePage === "home" && (
            <div className="support-home">
              <h1>Support Center</h1>
              <h2>Proscope Ticket System (PTS)</h2>
              <div className="support-card">
                <p>Welcome to the Support Center</p>
                <p className="secondary-text">
                  In order to streamline support requests and better serve you,
                  we utilize a support ticket system. Every support request is
                  assigned a unique ticket number which you can use to track the
                  progress and responses online. For your reference, we provide
                  complete archives and history of all your support requests. A
                  valid email address is required to submit a ticket.
                </p>
                <div className="button-container">
                  <button
                    className="btn-check-status"
                    onClick={() => setActivePage("register")}
                  >
                    Register
                  </button>
                </div>
              </div>
            </div>
          )}

          {activePage === "sign-in" && (
            <div className="sign-in-container">
              <h2>Sign in to Proscope Co., Ltd</h2>
              <p>
                To better serve you, we encourage our Clients to register for an
                account.
              </p>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  id="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  id="password"
                  placeholder="Enter your password"
                  value={formData.password || ""}
                  onChange={handleInputChange}
                />
              </div>
              <button className="submit-button" onClick={handleSignin}>
                Sign In
              </button>
              <br />
              <br />
              <p>
                If this is your first time contacting us or you've lost the
                ticket number, please or{" "}
                <button
                  className="link-button"
                  onClick={() => setActivePage("register")}
                >
                  register for an account
                </button>{" "}
                I'm an agent{" "}
                <button
                  className="link-button"
                  onClick={() => window.open("/admin", "_blank")}
                >
                  sign in here
                </button>{" "}
                or{" "}
                <button
                  className="link-button"
                  onClick={() =>
                    window.open("/manuals/user-guide.pdf", "_blank")
                  }
                >
                  manual
                </button>
                <br /> Did you{" "}
                <button
                  className="link-button"
                  onClick={() => navigate("/resetpassword")}
                >
                  forget your password?
                </button>
              </p>
            </div>
          )}

          {activePage === "register" && (
            <div className="register-container">
              <p>Please enter your email to receive an OTP.</p>
              <div className="input-group">
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="input-field"
                  disabled={isOtpSent && !isOtpExpired}
                />
              </div>

              {(!isOtpSent || isOtpExpired) && (
                <button className="submit-button" onClick={handleEmailSubmit}>
                  {isOtpExpired ? "Resend OTP" : "Send OTP"}
                </button>
              )}

              {isOtpSent && !isOtpExpired && (
                <p className="timer-text">
                  Please wait until the OTP expires before requesting a new one.
                </p>
              )}
            </div>
          )}

          {isOtpSent && activePage === "register" && (
            <div className="otp-container">
              <p>Enter the OTP sent to your email.</p>
              {isOtpExpired ? (
                <p className="error-message">
                  The OTP has expired. Please refresh and request a new OTP.
                </p>
              ) : (
                <p className="timer-text">{`Time left: ${timer}s`}</p>
              )}

              <div className="input-group">
                <input
                  type="text"
                  value={otp}
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) => {
                    const otpValue = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6);

                    setOtp(otpValue);
                    setOtpError("");
                  }}
                  placeholder="Enter OTP"
                  className="input-field"
                />
              </div>

              {otpError && <p className="error-message">{otpError}</p>}

              <button
                className="submit-button"
                onClick={handleOtpSubmit}
                disabled={isOtpExpired || otp.length !== 6}
              >
                Verify OTP
              </button>
            </div>
          )}

          {activePage === "form" && (
            <div className="form-container">
              <p>Please fill in your details to complete registration.</p>

              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={formData.email}
                  readOnly
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label>Full Name:</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label>Position:</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  placeholder="Enter your Position"
                  className="input-field"
                />
              </div>
              <div className="form-group">
                <label>Tax ID / เลขทะเบียนบริษัท (13 หลัก):</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    type="text"
                    name="taxno"
                    value={formData.taxno || ""}
                    maxLength={13}
                    inputMode="numeric"
                    onChange={(e) => {
                      const taxValue = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 13);

                      setFormData((prev) => ({
                        ...prev,
                        taxno: taxValue,
                        companyId: "",
                        companyName: "",
                        companyManual: "",
                      }));

                      setOptions((prev) => ({
                        ...prev,
                        company: [],
                      }));

                      setAllowCompanySelect(false);
                      setShowNewCompanyInput(false);
                      setCompanySearchStatus("idle");
                    }}
                    placeholder="เช่น 1234567890123"
                    className="input-field"
                    style={{ flex: 1 }}
                  />

                  <button
                    type="button"
                    className="submit-button"
                    onClick={handleSearchCompany}
                    disabled={loadingCompany}
                    style={{
                      whiteSpace: "nowrap",
                      opacity: loadingCompany ? 0.6 : 1,
                    }}
                  >
                    ค้นหา
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Company:</label>

                {companySearchStatus === "idle" && (
                  <p className="company-help-text">
                    กรุณากรอก Tax ID 13 หลักและกดค้นหาก่อน
                  </p>
                )}

                {companySearchStatus === "found" && (
                  <p className="company-success-text">
                    พบบริษัทในระบบ กรุณาตรวจสอบหรือเลือกรายการบริษัท
                  </p>
                )}

                {companySearchStatus === "not-found" && (
                  <p className="company-warning">
                    ไม่พบบริษัทจาก Tax ID นี้ กรุณากรอกชื่อบริษัทใหม่
                  </p>
                )}

                <div className="company-container">
                  {companySearchStatus === "found" && (
                    <select
                      className="input-field"
                      disabled={!allowCompanySelect}
                      value={formData.companyId || ""}
                      onChange={(e) => {
                        const selectedId = Number(e.target.value);

                        const selectedCompany = options.company.find(
                          (item) => Number(item.id) === selectedId,
                        );

                        setFormData((prev) => ({
                          ...prev,
                          companyId: selectedCompany?.id || "",
                          companyName: selectedCompany?.name || "",
                          companyManual: "",
                        }));
                      }}
                    >
                      <option value="">Select company</option>

                      {options.company.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  )}

                  {showNewCompanyInput && (
                    <input
                      type="text"
                      name="companyManual"
                      value={formData.companyManual || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          companyManual: e.target.value,
                          companyId: "",
                          companyName: "",
                        }))
                      }
                      placeholder="กรอกชื่อบริษัท"
                      className="input-field"
                    />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  className="input-field"
                />
              </div>
              <div className="form-group">
                <label>Address:</label>

                {options.address.length > 0 ? (
                  <>
                    <input
                      type="text"
                      name="addressManual"
                      value={formData.addressManual || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          addressManual: e.target.value,
                          address: "",
                        }))
                      }
                      placeholder="Enter your address manually"
                      className="input-field"
                    />

                    {/* <div className="or-divider">
                      <span className="or-text">หรือ</span>
                    </div>

                    <select
                      name="address"
                      value={formData.address}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          address: e.target.value,
                          addressManual: "",
                        }))
                      }
                      className="input-field"
                    >
                      <option value="">Select your address</option>
                      {options.address.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select> */}
                  </>
                ) : (
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your address"
                    className="input-field"
                  />
                )}
              </div>

              <div className="form-group">
                <label>Time Zone:</label>
                <select
                  name="timeZone"
                  value={formData.timeZone}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="">Select Time Zone</option>
                  <option value="utc-5">UTC-5</option>
                  <option value="utc+1">UTC+1</option>
                  <option value="utc+7">UTC+7</option>
                </select>
              </div>

              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                />
                {passwordError && (
                  <span className="error-message">{passwordError}</span>
                )}
              </div>

              <div className="form-group">
                <label>Confirm Password:</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm your password"
                />
                {confirmPasswordError && (
                  <span className="error-message">{confirmPasswordError}</span>
                )}
              </div>
              <button
                type="button"
                className="submit-button"
                onClick={handleFormSubmit}
                disabled={isRegistering}
              >
                {isRegistering ? "Registering..." : "Register"}
              </button>
            </div>
          )}

          {activePage === "agent-sign-in" && (
            <div className="sign-in-container">
              <h2>Admin and Agent Panels</h2>
              <p>Please sign in to access the admin and agent panels.</p>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  id="email2"
                  placeholder="Enter your email"
                  value={formData.email || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  id="password2"
                  placeholder="Enter your password"
                  value={formData.password || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </div>
              <button
                className="submit-button"
                onClick={() => window.open("/support/agent", "_blank")}
              >
                {" "}
                Sign In
              </button>
            </div>
          )}
        </main>
        {signinPopup.show && (
          <div className="signin-popup-overlay">
            <div className="signin-popup-modal">
              <h3>{signinPopup.title}</h3>

              <p>{signinPopup.message}</p>

              <div className="signin-popup-actions">
                <button
                  type="button"
                  className={`signin-popup-btn ${
                    signinPopup.type === "success" ? "success" : "error"
                  }`}
                  onClick={closeSigninPopup}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
        {registerPopup.show && (
          <div className="register-alert-overlay">
            <div className="register-alert-modal">
              <h3>{registerPopup.title}</h3>

              <p>{registerPopup.message}</p>

              <div className="register-alert-actions">
                <button
                  type="button"
                  className={`register-alert-btn ${
                    registerPopup.type === "success" ? "success" : "error"
                  }`}
                  onClick={closeRegisterPopup}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SupportCenter;
